import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { licensePlanApi, enumsApi } from '../services/api';
import { showError } from '../utils/showError';
import {
  PageHeader,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormField,
  FormTextarea,
  FormCheckbox,
  FormGroup,
} from '../components/common';
import { getModuleColor } from '../constants/licenses';

const CreateLicensePlan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [moduleTypes, setModuleTypes] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState({
    name: '',
    planCode: '',
    description: '',
    maxUsers: '',
    maxFloors: '',
    maxUnits: '',
    allowedModules: [],
    maxAIMonthlyBudget: '',
    monthlyPrice: '',
    annualPrice: '',
    expirationDays: '',
    displayOrder: 0,
  });

  useEffect(() => {
    const fetchModuleTypes = async () => {
      try {
        const response = await enumsApi.getModuleTypes();
        setModuleTypes(response.data || []);
      } catch (err) {
        console.error('Error fetching module types:', err);
      }
    };
    fetchModuleTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleModuleToggle = (moduleValue) => {
    setForm((prev) => ({
      ...prev,
      allowedModules: prev.allowedModules.includes(moduleValue)
        ? prev.allowedModules.filter((m) => m !== moduleValue)
        : [...prev.allowedModules, moduleValue],
    }));
  };

  const validate = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.planCode.trim()) errors.planCode = 'Plan code is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        planCode: form.planCode.trim(),
        description: form.description.trim() || null,
        maxUsers: form.maxUsers !== '' ? parseInt(form.maxUsers) : null,
        maxFloors: form.maxFloors !== '' ? parseInt(form.maxFloors) : null,
        maxUnits: form.maxUnits !== '' ? parseInt(form.maxUnits) : null,
        allowedModules: form.allowedModules,
        maxAIMonthlyBudget: form.maxAIMonthlyBudget !== '' ? parseFloat(form.maxAIMonthlyBudget) : null,
        monthlyPrice: form.monthlyPrice !== '' ? parseFloat(form.monthlyPrice) : null,
        annualPrice: form.annualPrice !== '' ? parseFloat(form.annualPrice) : null,
        expirationDays: form.expirationDays !== '' ? parseInt(form.expirationDays) : null,
        isActive: true,
        displayOrder: parseInt(form.displayOrder) || 0,
      };
      await licensePlanApi.create(payload);
      navigate('/license-plans');
    } catch (err) {
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Create License Plan"
        backLink={{ label: 'Back to License Plans', href: '/license-plans' }}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader
            title="Plan Information"
            subtitle="Define the license plan details and limits"
          />
          <CardBody>
            <FormGroup title="Basic Information">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  label="Name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  error={formErrors.name}
                  placeholder="e.g. Premium Plan"
                />
                <FormField
                  label="Plan Code"
                  name="planCode"
                  value={form.planCode}
                  onChange={handleChange}
                  required
                  error={formErrors.planCode}
                  placeholder="e.g. PREMIUM"
                  helpText="Unique identifier, cannot be changed later"
                />
              </div>
              <FormTextarea
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={2}
                placeholder="Optional plan description..."
              />
            </FormGroup>

            <FormGroup title="Limits" description="Leave empty for unlimited">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <FormField
                  label="Max Users"
                  name="maxUsers"
                  type="number"
                  value={form.maxUsers}
                  onChange={handleChange}
                  placeholder="Unlimited"
                  min="0"
                />
                <FormField
                  label="Max Floors"
                  name="maxFloors"
                  type="number"
                  value={form.maxFloors}
                  onChange={handleChange}
                  placeholder="Unlimited"
                  min="0"
                />
                <FormField
                  label="Max Units"
                  name="maxUnits"
                  type="number"
                  value={form.maxUnits}
                  onChange={handleChange}
                  placeholder="Unlimited"
                  min="0"
                />
              </div>
            </FormGroup>

            <FormGroup title="Allowed Modules" description="Select the modules available with this plan">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {moduleTypes.map((mod) => (
                  <FormCheckbox
                    key={mod.value}
                    label={
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getModuleColor(mod.value)}`}>
                        {mod.displayName || mod.name}
                      </span>
                    }
                    checked={form.allowedModules.includes(mod.value)}
                    onChange={() => handleModuleToggle(mod.value)}
                  />
                ))}
              </div>
            </FormGroup>

            <FormGroup title="Pricing & Duration">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                <FormField
                  label="Monthly Price"
                  name="monthlyPrice"
                  type="number"
                  value={form.monthlyPrice}
                  onChange={handleChange}
                  placeholder="Free"
                  min="0"
                  step="0.01"
                />
                <FormField
                  label="Annual Price"
                  name="annualPrice"
                  type="number"
                  value={form.annualPrice}
                  onChange={handleChange}
                  placeholder="Free"
                  min="0"
                  step="0.01"
                />
                <FormField
                  label="Expiration Days"
                  name="expirationDays"
                  type="number"
                  value={form.expirationDays}
                  onChange={handleChange}
                  placeholder="No expiration"
                  helpText="Days until license expires after activation"
                  min="1"
                />
              </div>
            </FormGroup>

            <FormGroup title="Advanced">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  label="Max AI Monthly Budget"
                  name="maxAIMonthlyBudget"
                  type="number"
                  value={form.maxAIMonthlyBudget}
                  onChange={handleChange}
                  placeholder="No limit"
                  helpText="In dollars"
                  min="0"
                  step="0.01"
                />
                <FormField
                  label="Display Order"
                  name="displayOrder"
                  type="number"
                  value={form.displayOrder}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                />
              </div>
            </FormGroup>
          </CardBody>
        </Card>

        <div className="flex justify-end space-x-3">
          <Button type="button" onClick={() => navigate('/license-plans')} variant="secondary">
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={loading} disabled={loading}>
            Create Plan
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateLicensePlan;
